import { useEffect } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { usePageViewAnalytics } from '@leagueplatform/analytics';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppointmentIframeModal } from 'components/modals/appointment-iframe-modal.component';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { parseValidHttpsUrl } from 'common/parse-valid-https-url';
import {
  clearBookingIframeSession,
  clearStaleBookingIframeSession,
  persistBookingIframeSession,
  readStoredBookingIframeSession,
  stripBookingLinkFromLocationState,
} from 'utils/book-appointment-location-state';

/**
 * Renders the booking iframe modal when there is a valid HTTPS `bookingLink`
 * from router state (masonry action) or from tab {@link sessionStorage} for the
 * same route (survives refresh). Storage is cleared when the user closes or
 * completes the flow ({@link clearBookingIframeSession}).
 */
const BookAppointmentModalOpenShell = ({
  bookingLink,
  onClose,
}: {
  bookingLink: string;
  onClose: () => void;
}) => {
  const { formatMessage } = useIntl();

  usePageViewAnalytics({ screen_name: 'Book Appointment' });
  useDocumentTitle(formatMessage({ id: 'STR_BOOK_APPOINTMENT' }));

  return <AppointmentIframeModal bookingLink={bookingLink} onClose={onClose} />;
};

export const BookAppointmentModalHost = () => {
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const currentReturnPath = `${pathname}${search}`;

  const bookingLinkFromLocation = parseValidHttpsUrl(
    typeof state === 'object' && state !== null && 'bookingLink' in state
      ? (state as { bookingLink?: unknown }).bookingLink
      : undefined,
  );

  const bookingLinkFromSession =
    readStoredBookingIframeSession(currentReturnPath);

  const bookingLink = bookingLinkFromLocation ?? bookingLinkFromSession;

  useEffect(() => {
    if (bookingLinkFromLocation) {
      persistBookingIframeSession(bookingLinkFromLocation, currentReturnPath);
    }
  }, [bookingLinkFromLocation, currentReturnPath]);

  useEffect(() => {
    clearStaleBookingIframeSession(currentReturnPath);
  }, [currentReturnPath]);

  const showBookingModal = bookingLink !== undefined;

  const handleClose = () => {
    clearBookingIframeSession();
    const cleanedState = stripBookingLinkFromLocationState(state);
    navigate(APP_MANAGED_ROUTES.HOME, {
      replace: true,
      ...(cleanedState !== undefined && { state: cleanedState }),
    });
  };

  if (!showBookingModal) {
    return null;
  }

  return (
    <BookAppointmentModalOpenShell
      bookingLink={bookingLink}
      onClose={handleClose}
    />
  );
};
